import {
  FormControl,
  FormHelperText,
  Input,
  styled,
  Typography
} from '@mui/material'
import * as R from 'ramda'
import { ChangeEvent, FC, forwardRef, Ref, useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import NumberFormat, {
  InputAttributes,
  NumberFormatValues
} from 'react-number-format'

const StyledInput = styled(Input)`
  &.MuiInputBase-root {
    &.MuiInput-root {
      :before {
        border-bottom: unset;
      }
      :after {
        border-bottom: unset;
      }
    }
  }
  .MuiInputBase-input {
    background-color: #0b0b0c;
    border-radius: 2px;
    color: white;
    font-size: 16px;
    font-weight: 400;
    height: 35px;
    padding: 0px 10px;
    text-align: center;
    :focus {
      border: 1px solid #ffbf36;
    }
    &.Mui-disabled {
      background-color: #ffffff09;
      -webkit-text-fill-color: #ffffff55;
    }
  }
`

const StartAdornment = styled(Typography)`
  color: white;
  font-size: 16px;
  font-weight: 300;
  padding-right: 11px;
`

const StyledFormHelperText = styled(FormHelperText)`
  color: #b1b3b3;
  font-size: 12px;
  font-weight: 300;
  letter-spacing: 0.5px;
  text-align: right;
  margin-right: 0px;

  strong {
    color: #ffbf36;
    font-weight: 500;
    letter-spacing: 1px;
  }
`

const QuantityNumberFormat = forwardRef<
  NumberFormat<InputAttributes>,
  QuantityNumberFormatProps
>(function NumberFormatCustom(props: QuantityNumberFormatProps, ref: Ref<any>) {
  const { max, min, onChange, ...rest } = props

  const handleOnIsAllowed = useCallback(
    (values: NumberFormatValues) => {
      const { value } = values

      const numValue = Number(value)

      return numValue >= min && numValue <= max
    },
    [max, min]
  )

  const handleOnValueChange = useCallback(
    (values: NumberFormatValues) => {
      onChange({ target: { value: values.value } })
    },
    [onChange]
  )

  return (
    <NumberFormat<unknown>
      {...rest}
      allowLeadingZeros={false}
      decimalScale={0}
      getInputRef={ref}
      thousandSeparator
      isAllowed={handleOnIsAllowed}
      onValueChange={handleOnValueChange}
    />
  )
})

const QuantityInput: FC<Props> = (props: Props) => {
  const {
    allowInput = true,
    className,
    quantifier,
    total,
    value,
    onChange
  } = props

  const { t } = useTranslation()

  const [userInput, setUserInput] = useState<string>('')

  const isEmpty = R.isEmpty(userInput) && value === 0

  const handleOnBlur = useCallback(() => {
    if (R.isEmpty(userInput)) {
      setUserInput('0')
    }
  }, [userInput])

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value

      setUserInput(newValue)
      onChange(Number(newValue))
    },
    [onChange]
  )

  return (
    <FormControl className={className}>
      <StyledInput
        className='Custom-Input'
        disabled={!allowInput}
        inputComponent={QuantityNumberFormat as any} // we use react-number-format to validate mui input
        inputProps={{ min: 0, max: total }}
        startAdornment={
          <StartAdornment className='Custom-Adornment'>
            {quantifier}
          </StartAdornment>
        }
        value={isEmpty ? '' : value}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
      />
      <StyledFormHelperText className='Custom-Helper-Text'>
        <Trans>{t('quantity_input.total', { total })}</Trans>
      </StyledFormHelperText>
    </FormControl>
  )
}

type QuantityNumberFormatProps = {
  max: number
  min: number
  onChange: (event: { target: { value: string } }) => void
}

type Props = {
  allowInput?: boolean
  className?: string
  quantifier: string
  total: number
  value: number
  onChange: (value: number) => void
}

export default QuantityInput
